/**
 * Structured Logging Utility
 *
 * Provides environment-aware logging that:
 * - Suppresses debug/info logs in production
 * - Preserves error/warn logs always
 * - Adds consistent prefixing for easier filtering
 * - Supports structured data logging
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.debug('Fetching data', { userId });
 *   logger.info('Operation complete');
 *   logger.warn('Deprecated API used');
 *   logger.error('Failed to save', error);
 *
 * @module lib/logger
 */

interface LogData {
  [key: string]: unknown;
}

const isDevelopment = import.meta.env.DEV;

/**
 * Formats log message with optional structured data
 */
function formatMessage(message: string, data?: LogData): string {
  if (!data || Object.keys(data).length === 0) {
    return message;
  }
  return `${message} ${JSON.stringify(data)}`;
}

/**
 * Structured logger with environment-aware output
 */
export const logger = {
  /**
   * Debug level - only shown in development
   * Use for detailed diagnostic information
   */
  debug(message: string, data?: LogData): void {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug(`[DEBUG] ${formatMessage(message, data)}`);
    }
  },

  /**
   * Info level - only shown in development
   * Use for general operational information
   */
  info(message: string, data?: LogData): void {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.info(`[INFO] ${formatMessage(message, data)}`);
    }
  },

  /**
   * Warn level - always shown
   * Use for potentially problematic situations
   */
  warn(message: string, data?: LogData): void {
    console.warn(`[WARN] ${formatMessage(message, data)}`);
  },

  /**
   * Error level - always shown
   * Use for error conditions that should be investigated
   */
  error(message: string, error?: unknown, data?: LogData): void {
    const errorData = error instanceof Error
      ? { ...data, errorMessage: error.message, errorStack: error.stack }
      : { ...data, error };
    console.error(`[ERROR] ${formatMessage(message, errorData)}`);
  },
};

export default logger;
